/**
 * Button component
 *
 * Optional props:
 * title : string - title of box
 * close : function - parent function to close alert
 * action : function - primary action button
 * actionText : string - text for primary action button
 * secondaryAction : function - secondary action button
 * secondaryActionText : string - text for secondary action button
 * type : string - success, warning or error
 * success : boolean - success style
 * warning : boolean - warning style
 * error : boolean - error style
 * icon : string - show icon
 * width : string - width of box (with unit)
 * fixed : boolean - if the alert box should be fixed and centered on page
 *
 * Examples for component:
 * <Alert title="Lorem">This is the content</Alert>
 * <Alert title="Success with only title and icon" success icon="anyString" />
 * <Alert>Only content</Alert>
 *
 */

import React from 'react';
import styled from 'styled-components';
import { Button } from '../';
import { colors } from '../../variables';
import closeImage from '../../../images/close_light_grey.svg';
import closeImageWhite from '../../../images/close_white.svg';
import approveImage from '../../../images/approve.svg';
import errorImage from '../../../images/error-alert.svg';
import informationImage from '../../../images/information.svg';

const getBackground = props => {
	if (props.success || props.type === 'success') return colors.green.light;
	if (props.warning || props.type === 'warning') return colors.yellow.bromma;
	if (props.error || props.type === 'error') return colors.red.light;
	if (props.blue) return colors.blue.brommaDarker;
	return '#fff';
};

const getBorderColor = props => {
	if (props.success || props.type === 'success') return colors.green.ecoLight;
	if (props.warning || props.type === 'warning') return colors.yellow.bromma;
	if (props.error || props.type === 'error') return colors.red.warning;
	if (props.blue) return colors.blue.brommaDarker;
	return colors.grey.normal;
};

const getIcon = props => {
	if (props.success || props.type === 'success') return approveImage;
	if (props.error || props.type === 'error') return errorImage;
	if (props.information || props.type === 'information')
		return informationImage;
	return '';
};

const getWidth = props => {
	if (props.width) return props.width;
	if (props.fixed) return '500px';
	return 'unset';
};

const getCloseIcon = props => {
	if (props.blue) return closeImageWhite;
	return closeImage;
};

const Container = styled.div`
	${props =>
		props.fixed &&
		`
			position: fixed;
			top: 0;
			left: 0;
			height: 100%;
			width: 100%;
			z-index: 200;
			background: rgba(0, 0, 0, 0.7);
	`};
`;

const AlertBox = styled.div`
	position: relative;
	width: ${props => getWidth(props)};
	padding: ${props => (props.icon ? '20px 20px 20px 90px' : '20px')};
	border: 1px solid ${props => getBorderColor(props)};
	background: ${props => getBackground(props)};
	color: #fff;
	line-height: 1.4;
	white-space: pre-line;

	${props =>
		props.fixed &&
		`
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			max-height: 100vh;
			max-width: calc(100% - 40px);
			overflow-y: auto;
	`};
`;

const AlertIcon = styled.div`
	position: absolute;
	left: 20px;
	top: 50%;
	transform: translateY(-50%);
	background: url(${props => getIcon(props)});
	display: block;
	background-repeat: no-repeat;
	background-size: contain;
	background-position: center;
	width: ${props =>
		props.success || props.type === 'success' ? '30px' : '40px'};
	height: 30px;
`;

const AlertTitle = styled.h3`
	color: #777676;
	margin-top: 0;
	margin-bottom: 10px;
	font-size: 18px;
	letter-spacing: 0.3px;
	padding-right: ${props => (props.close ? '30px' : '0')};
	display: ${props => (props.title ? 'none' : 'block')};
`;

const AlertContent = styled.div`
	color: #777676;
	margin: 0;
`;

const AlertList = styled.ul`
	color: #777676;
	margin: 0;
	padding: 0;
	padding-left: 10px;

	li {
		margin: 0;

		&:not(:last-of-type) {
			margin-bottom: 5px;
		}
	}
`;

const CloseButton = styled.button`
	border: 0;
	border-radius: 0;
	background-image: url(${props => getCloseIcon(props)});
	background-color: transparent;
	background-position: center;
	background-size: 16px;
	background-repeat: no-repeat;
	position: absolute;
	padding: 0;
	top: 15px;
	right: 15px;
	width: 20px;
	height: 20px;
	cursor: pointer;
`;

class Alert extends React.Component {
	componentDidMount() {
		if (this.props.fixed) {
			document.addEventListener('keydown', this.onEscape);
		}
	}

	componentWillUnmount() {
		if (this.props.fixed) {
			document.removeEventListener('keydown', this.onEscape);
		}
	}

	onEscape = ({ keyCode }) => {
		const enterKeyCode = 27;

		if (keyCode === enterKeyCode) {
			this.props.close();
		}
	};

	renderActionButton() {
		const {
			action,
			actionText,
			actionType,
			actionDisabled = false,
		} = this.props;

		if (!action || !actionText) return null;

		return (
			<Button
				type="button"
				onClick={() => action()}
				className="mt10"
				buttonType={actionType}
				disabled={actionDisabled}
			>
				{actionText}
			</Button>
		);
	}

	renderSecondaryActionButton() {
		const { secondaryAction, secondaryActionText } = this.props;

		if (!secondaryAction || !secondaryActionText) return null;

		return (
			<Button
				inline
				type="button"
				onClick={() => secondaryAction()}
				className="mt10 ml10"
			>
				{secondaryActionText}
			</Button>
		);
	}

	render() {
		const {
			title,
			children,
			className,
			list,
			fixed,
			width,
			action,
			close,
			...rest
		} = this.props;

		if (!title && !children && !list) return null;

		return (
			<Container fixed={fixed} onClick={close}>
				<AlertBox
					className={className}
					fixed={fixed}
					width={width}
					onClick={e => e.stopPropagation()}
					{...rest}
				>
					{title && <AlertTitle {...rest}>{title}</AlertTitle>}

					{close && (
						<CloseButton type="button" onClick={() => close()} {...rest} />
					)}

					{children && <AlertContent {...rest}>{children}</AlertContent>}

					{this.props.icon && <AlertIcon {...rest} />}

					{list && (
						<AlertList {...rest}>
							{list.map((item, index) => <li key={index}>{item}</li>)}
						</AlertList>
					)}

					{this.renderActionButton()}

					{this.renderSecondaryActionButton()}
				</AlertBox>
			</Container>
		);
	}
}

export default Alert;



// WEBPACK FOOTER //
// ./src/components/Common/Alert/Alert.js